<script setup lang="ts">
//#region imports
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { isErrorResponse, type EmitsToListeners, type FormErrors } from '@/types/core';
//#endregion

//#region types
interface Props {
  sitePlayerId: string;
  playerId: string;
  isVirtual: boolean;
}

interface Emits {
  success: [];
  close: [];
}

export interface MarkPlayerVirtualParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
//#endregion

//#region state
const captcha = ref('');
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref<FormErrors>({});
//#endregion

//#region computed
const isCaptchaMatched = computed(() => {
  return t('players.playersList.confirm.yes').toLowerCase()
    === captcha.value.toLowerCase();
});

const title = computed(() => {
  const suffix = props.isVirtual
    ? t('players.playersList.confirm.to_real')
    : t('players.playersList.confirm.to_virtuals');

  return `${t('players.playersList.confirm.title')} ${props.sitePlayerId} ${suffix}`;
});

const info = computed(() => {
  return t('players.playersList.confirm.msg_1', {
    player_id: `<b>${props.sitePlayerId}</b>`,
    yes: `<b>${t('players.playersList.confirm.yes')}</b>`,
  });
});
//#endregion

//#region methods
function open() {
  isVisible.value = true;
}

function handleClose() {
  isVisible.value = false;
  emit('close');
}

async function handleSave() {
  if (!props.playerId || !isCaptchaMatched.value) {
    return;
  }

  isLoading.value = true;
  errors.value = {};
  try {
    await api.markAsVirtual(props.playerId);
    emit('success');
    isVisible.value = false;
  } catch (error) {
    if (isErrorResponse(error)) {
      errors.value = error.data.errors;
    }
  } finally {
    isLoading.value = false;
  }
}
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="title"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:flex">
        <UiIcon
          name="exclamation-triangle"
          :size="24"
          color="var(--danger-color)"
          class="t:mr-12"
        />
        <div
          class="confirm-popup__message"
          v-html="$t('players.playersList.confirm.msg')"
        />
      </div>
      <div
        class="confirm-popup__message t:mt-16"
        v-html="info"
      />
      <UiInput
        v-model="captcha"
        class="t:mt-16"
        size="big"
        width="100%"
        :placeholder="$t('players.playersList.confirm.yes')"
      />
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-10">
        <UiButton
          type="negative"
          icon="times"
          size="big"
          :disabled="isLoading"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          icon="check"
          size="big"
          :disabled="!isCaptchaMatched || isLoading"
          :loading="isLoading"
          @click="handleSave"
        >
          {{ $t('players.playersList.change') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
